import { useEffect } from 'react';

/**
 * Lightweight per-page SEO hook.
 *
 * Updates <title>, <meta name="description">, Open Graph / Twitter tags
 * and canonical link. Set `noindex: true` for investor / private routes
 * so search engines exclude them.
 *
 *   useSEO({ title: '...', description: '...', path: '/about' });
 *   useSEO({ title: '...', description: '...', path: '/investors', noindex: true });
 */
export function useSEO({ title, description, path = '', image, noindex = false }) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (attr, name, content) => {
      if (content === undefined || content === null) return;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const canonical = `https://keystoneconsulting.uk${path}`;
    setMeta('name', 'description', description);
    setMeta('name', 'title', title);
    setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');

    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    if (image) setMeta('property', 'og:image', image);

    setMeta('property', 'twitter:title', title);
    setMeta('property', 'twitter:description', description);
    setMeta('property', 'twitter:url', canonical);
    if (image) setMeta('property', 'twitter:image', image);

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
  }, [title, description, path, image, noindex]);
}
